import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface RejectConfirmationModalProps {
  closeModal: () => void;
  handleRejectApplication: () => void;
  handleRejectSession: () => void;
  loading?: boolean;
  msg: string;
  applicationStatus?: string;
}

function RejectConfirmationModal({
  closeModal,
  handleRejectApplication,
  handleRejectSession,
  loading = false,
  msg,
  applicationStatus,
}: RejectConfirmationModalProps) {
  const { t } = useTranslation();
  const [loadingButton, setLoadingButton] = useState<"application" | "session" | null>(null);

  // Enable "Reject the Session" button only if user has a confirmed seat
  const canRejectSession =
    applicationStatus === "DRAFTED" ||
    applicationStatus === "CONFIRMED_BY_LEARNER" ||
    applicationStatus === "CONFIRMED_BY_HR" ||
    applicationStatus === "CONFIRMED_BY_MANAGER";

  const handleApplicationClick = async () => {
    setLoadingButton("application");
    try {
      await handleRejectApplication();
    } finally {
      setLoadingButton(null);
    }
  };

  const handleSessionClick = async () => {
    setLoadingButton("session");
    try {
      await handleRejectSession();
    } finally {
      setLoadingButton(null);
    }
  };

  // Reset loading state when parent loading becomes false
  useEffect(() => {
    if (!loading && loadingButton !== null) {
      setLoadingButton(null);
    }
  }, [loading, loadingButton]);

  const isProcessing = loading || loadingButton !== null;
  const isApplicationLoading = loadingButton === "application";
  const isSessionLoading = loadingButton === "session";

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-5 rounded-lg shadow-xl flex flex-col w-[90%] sm:w-[450px] max-w-[90vw]">
        <div className="flex flex-col items-start justify-start gap-2 w-full mb-5">
          <h2 className="text-base font-semibold text-red-600">
            {t("Confirm Action")}
          </h2>
          <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line" dangerouslySetInnerHTML={{ __html: msg }} />
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="px-3 py-1.5 text-xs font-normal text-white bg-gray-500 rounded hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleApplicationClick}
            disabled={isProcessing}
          >
            {isApplicationLoading
              ? `${t("Processing")}...`
              : t("DELETE APPLICATION")}
          </button>
          <button
            className="px-3 py-1.5 text-xs font-normal text-white bg-red-500 rounded hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSessionClick}
            disabled={isProcessing || !canRejectSession}
            title={
              !canRejectSession
                ? t("You can only reject a session if you have a confirmed seat")
                : ""
            }
          >
            {isSessionLoading
              ? `${t("Processing")}...`
              : t("REJECT SEAT")}
          </button>
          <button
            className="px-3 py-1.5 text-xs font-normal text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={closeModal}
            disabled={isProcessing}
          >
            {t("GO BACK")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RejectConfirmationModal;

